import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, X } from "lucide-react";

const CONSENT_KEY = "jobtimehr_cookie_consent_v1";

export const readConsent = () => {
  try { return JSON.parse(localStorage.getItem(CONSENT_KEY) || "null"); }
  catch { return null; }
};

const CATEGORIES = [
  { key: "preferences", label: "Preferenze", desc: "Ricordano le tue scelte (es. società selezionata) per un'esperienza personalizzata." },
  { key: "statistics", label: "Statistiche", desc: "Ci aiutano a capire come viene usato il sito in forma aggregata e anonima." },
  { key: "marketing", label: "Marketing", desc: "Utilizzati per proporre contenuti e messaggi in linea con i tuoi interessi." },
];

const Toggle = ({ checked, disabled, onChange, testid }) => (
  <button type="button" role="switch" aria-checked={checked} disabled={disabled} data-testid={testid}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-[#ff7e00]" : "bg-white/20"} ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ preferences: false, statistics: false, marketing: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setVisible(true);
    else setPrefs({ preferences: !!existing.preferences, statistics: !!existing.statistics, marketing: !!existing.marketing });
    const openPrefs = () => {
      const cur = readConsent();
      if (cur) setPrefs({ preferences: !!cur.preferences, statistics: !!cur.statistics, marketing: !!cur.marketing });
      setVisible(true);
      setShowPrefs(true);
    };
    window.addEventListener("open-cookie-preferences", openPrefs);
    return () => window.removeEventListener("open-cookie-preferences", openPrefs);
  }, []);

  const persist = (choice) => {
    const value = { necessary: true, ...choice, ts: new Date().toISOString(), version: 1 };
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify(value)); } catch (e) { console.error("Salvataggio consenso non riuscito:", e); }
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => persist({ preferences: true, statistics: true, marketing: true });
  const rejectAll = () => persist({ preferences: false, statistics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-[90] p-3 sm:p-4" data-testid="cookie-banner">
        <div className="mx-auto max-w-5xl rounded-2xl border border-white/15 bg-[#0d1b2f]/95 backdrop-blur-md shadow-2xl shadow-black/40 text-white overflow-hidden">
          <div className="p-5 sm:p-6">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <span className="hidden sm:inline-flex w-11 h-11 shrink-0 items-center justify-center rounded-xl bg-[#ff7e00]/15 text-[#ff7e00]"><Cookie size={22} /></span>
              <div className="flex-1">
                <h2 className="text-lg font-black">Rispettiamo la tua privacy</h2>
                <p className="text-[13.5px] text-gray-300 mt-1 leading-relaxed">
                  Usiamo cookie tecnici necessari al funzionamento del sito. Con il tuo consenso possiamo usare anche cookie di preferenze, statistica e marketing.
                  Consulta la <Link to="/cookie-policy" data-testid="cookie-banner-policy-link" className="text-[#ffb066] underline underline-offset-2 hover:text-[#ff7e00]">Cookie Policy</Link> e la <Link to="/privacy-policy" className="text-[#ffb066] underline underline-offset-2 hover:text-[#ff7e00]">Privacy Policy</Link>.
                </p>

                {showPrefs && (
                  <div className="mt-4 space-y-3" data-testid="cookie-preferences">
                    <div className="flex items-start justify-between gap-4 rounded-xl bg-white/5 border border-white/10 p-3">
                      <div>
                        <div className="flex items-center gap-2 text-[14px] font-bold"><Shield size={15} className="text-emerald-400" /> Necessari</div>
                        <p className="text-[12px] text-gray-400 mt-0.5">Indispensabili per login, sicurezza e funzionamento del sito. Sempre attivi.</p>
                      </div>
                      <Toggle checked disabled onChange={() => {}} testid="cookie-toggle-necessary" />
                    </div>
                    {CATEGORIES.map((c) => (
                      <div key={c.key} className="flex items-start justify-between gap-4 rounded-xl bg-white/5 border border-white/10 p-3">
                        <div>
                          <div className="text-[14px] font-bold">{c.label}</div>
                          <p className="text-[12px] text-gray-400 mt-0.5">{c.desc}</p>
                        </div>
                        <Toggle checked={prefs[c.key]} onChange={(v) => setPrefs((p) => ({ ...p, [c.key]: v }))} testid={`cookie-toggle-${c.key}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex flex-col sm:flex-row sm:justify-end gap-2.5">
              {!showPrefs && (
                <button onClick={() => setShowPrefs(true)} data-testid="cookie-customize-btn"
                  className="order-3 sm:order-1 inline-flex items-center justify-center rounded-full border border-white/25 hover:border-white/50 text-white font-bold text-[13.5px] px-5 py-2.5 transition-colors">
                  Personalizza
                </button>
              )}
              {showPrefs && (
                <button onClick={savePrefs} data-testid="cookie-save-prefs-btn"
                  className="order-1 inline-flex items-center justify-center rounded-full border border-white/25 hover:border-white/50 text-white font-bold text-[13.5px] px-5 py-2.5 transition-colors">
                  Salva preferenze
                </button>
              )}
              <button onClick={rejectAll} data-testid="cookie-reject-btn"
                className="order-2 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-[13.5px] px-5 py-2.5 transition-colors">
                Rifiuta non essenziali
              </button>
              <button onClick={acceptAll} data-testid="cookie-accept-btn"
                className="order-1 sm:order-3 inline-flex items-center justify-center rounded-full bg-[#ff7e00] hover:bg-[#e56f00] text-white font-bold text-[13.5px] px-6 py-2.5 shadow-lg shadow-[#ff7e00]/20 transition-colors">
                Accetta tutti
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
